import React from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ApolloQueryResult } from '@apollo/client';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import {
  CreditLineCard,
  HealthCheckCard,
  DocumentsCard,
  TasksCard,
  VendorOpportunity,
} from 'modules/vendorOpportunity';
import { ScoreCardV2 } from '../../ScoreCardV2';

interface Props {
  vo: VendorOpportunity;
  creditApp: any;
  userName: string;
  accountId: string;
  vendorProfile: any;
  creditAppRefetch: () => Promise<ApolloQueryResult<void>>;
  permissions: Record<'creditLine' | 'healthCheck' | 'docs' | 'tasks', boolean>;
  contentViewType?: any;
  refetchVO?: any;
  equipmentData?: any;
  refetchEquipment?: any;
  equipmentLoading?: boolean;
  userProfile?: any;
  account?: any;
  portalConfigurationDataLoading?: boolean;
  creditAppOwners?: any;
  isScorecardHiddenForUserRole?: boolean;
  showCreditLineCard?: boolean;
}

export const CreditLineTab = ({
  vo,
  userName,
  accountId,
  creditApp,
  permissions,
  vendorProfile,
  creditAppRefetch,
  contentViewType,
  refetchVO,
  equipmentData,
  refetchEquipment,
  equipmentLoading,
  userProfile,
  account,
  portalConfigurationDataLoading,
  creditAppOwners,
  isScorecardHiddenForUserRole,
  showCreditLineCard,
}: Props) => {
  const classes = useStyles();

  return (
    <Box className={classes.creditLineTab}>
      <RenderWithCondition condition={permissions.creditLine}>
        <CreditLineCard
          userName={userName}
          creditLineRequestLegacyData={{
            notes: vo?.creditLineNotes,
            status: vo?.creditLineStatus as any,
            VOAmount: vo?.creditLineAmountRequested ?? 0,
            creditAppAmount: creditApp?.creditLineAmountRequested ?? 0,
          }}
          VOId={vo?.vendorOpportunityId}
          isCreditLineRequested={vo.creditLineRequest?.length > 0 || creditApp.applyingFor?.includes('creditLine')}
          expirationDate={creditApp?.creditLineExpirationDate ?? null}
          creditLineRequest={vo?.creditLineRequest?.[vo?.creditLineRequest.length - 1] || null}
        />
      </RenderWithCondition>

      <RenderWithCondition condition={!isScorecardHiddenForUserRole && (showCreditLineCard ?? false)}>
        <ScoreCardV2
          contentViewType={contentViewType}
          vo={vo}
          cs={null}
          refetchVO={refetchVO}
          equipmentData={equipmentData}
          refetchEquipment={refetchEquipment}
          equipmentLoading={equipmentLoading}
          creditApplication={creditApp}
          vp={vendorProfile}
          userProfile={userProfile}
          account={account}
          refetchCreditApp={creditAppRefetch}
          portalConfigurationDataLoading={portalConfigurationDataLoading}
          creditAppOwners={creditAppOwners}
        />
      </RenderWithCondition>

      <RenderWithCondition condition={permissions.healthCheck && vendorProfile}>
        <HealthCheckCard
          userName={userName}
          creditApp={creditApp}
          cardTitle="Compliance"
          accountId={accountId}
          refetchCreditApp={creditAppRefetch}
          softPullInDealerPortal={
            vendorProfile ? vendorProfile.softPullInDealerPortal : 'CBC_EQUIFAX_SOFT_SUMMARY_ONLY'
          }
        />
      </RenderWithCondition>

      <RenderWithCondition condition={permissions.docs}>
        <DocumentsCard VOId={vo.vendorOpportunityId} />
      </RenderWithCondition>

      <RenderWithCondition condition={permissions.tasks}>
        <TasksCard VOId={vo.vendorOpportunityId} />
      </RenderWithCondition>
    </Box>
  );
};

const useStyles = makeStyles({
  creditLineTab: {
    gap: '1rem',
    display: 'flex',
    flexDirection: 'column',
  },
});
